import { defineStore } from 'pinia'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import {
  customerOwnerApi,
  customerOwnerContext,
} from '@/globals/typescript/utils/api/customerOwnerApi'
import { useAccountStore } from '@/stores/accountStore'
import { useUserStore } from '@/stores/userStore'
import type {
  ILabCustomerUserBusinessInvite,
  ILabCustomerUserInsuranceInvite,
} from '@team-milva/lab-models/invite'

/** A pending invite to the signed-in user's account — business or insurance (MIL-402). */
export type TPendingInvite = ILabCustomerUserBusinessInvite | ILabCustomerUserInsuranceInvite

// There is no lab-cloud endpoint to list pending invites (only create/revoke/
// get-by-token), so this reads the `Invites` collection directly via
// Firestore — allowed by the catch-all `allow read: if request.auth != null`
// rule, following the same onSnapshot pattern as every other store here.
export const useInviteStore = defineStore('invite', {
  state: () => ({
    pendingInvites: [] as TPendingInvite[],
    unsubscribePendingInvites: null as (() => void) | null,
  }),

  actions: {
    getPendingInvites(): Promise<TPendingInvite[]> {
      if (this.pendingInvitesLoaded) {
        return Promise.resolve(this.pendingInvites)
      }

      const accountStore = useAccountStore()
      const owner = customerOwnerContext(useUserStore().currentUser)

      return new Promise((resolve) => {
        if (!accountStore.customerAccount || !owner) {
          return resolve([])
        }

        let resolved = false

        const q = query(
          collection(db, 'Apps', '-lab-customer-', 'Invites'),
          // The account's own invites, business or insurance (MIL-402).
          where('customer.type', '==', owner.customer.type),
          where('customer.id', '==', accountStore.customerAccount.id),
          where('status', '==', 'pending')
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const invites: TPendingInvite[] = []
          querySnapshot.forEach((docSnap) => {
            invites.push(docSnap.data() as TPendingInvite)
          })
          this.pendingInvites = invites
          this.unsubscribePendingInvites = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(invites)
          }
        })
      })
    },

    async sendInvite(email: string): Promise<void> {
      // A business or an insurance account admin (MIL-402).
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) return

      const result = await customerOwnerApi(owner, 'post', '/invites', {
        body: { emails: [email] },
      })

      if (!result.success) throw new Error(result.code)
    },

    async revokeInvite(inviteID: string): Promise<void> {
      // A business or an insurance account admin (MIL-402).
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) return

      const result = await customerOwnerApi(owner, 'post', '/invites/revoke', {
        body: { inviteID },
      })

      if (!result.success) throw new Error(result.code)
    },

    resetStore(): void {
      this.pendingInvites = []

      if (this.unsubscribePendingInvites) {
        this.unsubscribePendingInvites()
        this.unsubscribePendingInvites = null
      }
    },
  },

  getters: {
    pendingInvitesLoaded: (state) => !!state.unsubscribePendingInvites,
    // The server never flips `status` on expiry (only checked at redemption
    // time), so an invite past `expiresAt` still reads as "pending" here.
    activePendingInvites: (state): TPendingInvite[] =>
      state.pendingInvites.filter((invite) => invite.timestamps.expiresAt >= Date.now()),
  },
})
